import React, { useState } from 'react';
import { Post, Comment } from '../types';
import styles from './PostCard.module.scss';

interface PostCardProps {
  post: Post;
  onLike: (postId: string) => void;
  onUnlike: (postId: string) => void;
  onComment: (postId: string, content: string) => void;
  onShare: (postId: string) => void;
  currentUserId: string;
}

const PostCard: React.FC<PostCardProps> = ({
  post,
  onLike,
  onUnlike,
  onComment,
  onShare,
  currentUserId,
}) => {
  const [showComments, setShowComments] = useState(false);
  const [newComment, setNewComment] = useState('');
  const [isSubmittingComment, setIsSubmittingComment] = useState(false);

  const isLiked = post.likes.includes(currentUserId);
  const isShared = false; // shares not implemented in backend yet

  const handleLike = () => {
    if (isLiked) {
      onUnlike(post._id);
    } else {
      onLike(post._id);
    }
  };

  const handleComment = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newComment.trim()) return;

    setIsSubmittingComment(true);
    try {
      await onComment(post._id, newComment);
      setNewComment('');
    } catch (error) {
      console.error('Error adding comment:', error);
    } finally {
      setIsSubmittingComment(false);
    }
  };

  const handleShare = () => {
    onShare(post._id);
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffInHours = Math.floor((now.getTime() - date.getTime()) / (1000 * 60 * 60));
    
    if (diffInHours < 1) return 'Just now';
    if (diffInHours < 24) return `${diffInHours}h ago`;
    if (diffInHours < 168) return `${Math.floor(diffInHours / 24)}d ago`;
    return date.toLocaleDateString();
  };

  return (
    <div className={styles.postCard}>
      <div className={styles.postHeader}>
        <div className={styles.postAuthor}>
          <div className={styles.authorAvatar}>
            <img 
              src="https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=80&h=80&fit=crop&crop=face" 
              alt="Author Avatar"
              onError={(e) => {
                const target = e.target as HTMLImageElement;
                target.src = "https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=80&h=80&fit=crop&crop=face";
              }}
            />
          </div>
          <div className={styles.authorInfo}>
            <span className={styles.authorName}>{post.user?.username}</span>
            <span className={styles.postTime}>{formatDate(post.createdAt)}</span>
          </div>
        </div>
        
        <div className={styles.postActionsMenu}>
          <button className={styles.menuBtn}>⋯</button>
        </div>
      </div>

      <div className={styles.postContent}>
        <p className={styles.postText}>{post.data}</p>
      </div>

      <div className={styles.postStats}>
        <div className={styles.statsItem}>
          <span className={styles.statIcon}>❤️</span>
          <span className={styles.statCount}>{post.likes.length}</span>
        </div>
        <div className={styles.statsItem}>
          <span className={styles.statIcon}>💬</span>
          <span className={styles.statCount}>{post.comments.length}</span>
        </div>
        <div className={styles.statsItem}>
          <span className={styles.statIcon}>🔄</span>
          <span className={styles.statCount}>0</span>
        </div>
      </div>

      <div className={styles.postActions}>
        <button 
          className={`${styles.actionBtn} ${isLiked ? styles.liked : ''}`}
          onClick={handleLike}
        >
          <span className={styles.actionIcon}>
            {isLiked ? '❤️' : '🤍'}
          </span>
          <span className={styles.actionText}>Like</span>
        </button>

        <button 
          className={styles.actionBtn}
          onClick={() => setShowComments(!showComments)}
        >
          <span className={styles.actionIcon}>💬</span>
          <span className={styles.actionText}>Comment</span>
        </button>

        <button 
          className={`${styles.actionBtn} ${isShared ? styles.shared : ''}`}
          onClick={handleShare}
        >
          <span className={styles.actionIcon}>
            {isShared ? '🔄' : '📤'}
          </span>
          <span className={styles.actionText}>Share</span>
        </button>
      </div>

      {showComments && (
        <div className={styles.commentsSection}>
          <div className={styles.commentsList}>
            {post.comments.map((comment: Comment) => (
              <div key={comment._id} className={styles.comment}>
                <div className={styles.commentAuthor}>
                  <img 
                    src="https://images.unsplash.com/photo-1494790108755-2616b612b786?w=64&h=64&fit=crop&crop=face" 
                    alt="Commenter Avatar"
                    className={styles.commentAvatar}
                    onError={(e) => {
                      const target = e.target as HTMLImageElement;
                      target.src = "https://images.unsplash.com/photo-1494790108755-2616b612b786?w=64&h=64&fit=crop&crop=face";
                    }}
                  />
                  <span className={styles.commentAuthorName}>
                    {comment.user.username}
                  </span>
                  <span className={styles.commentTime}>
                    {formatDate(comment.createdAt)}
                  </span>
                </div>
                <p className={styles.commentContent}>{comment.data}</p>
              </div>
            ))}
          </div>

          <form onSubmit={handleComment} className={styles.commentForm}>
            <div className={styles.commentInputContainer}>
              <img 
                src="https://images.unsplash.com/photo-1500648767791-00dcc994a43e?w=64&h=64&fit=crop&crop=face" 
                alt="Your Avatar"
                className={styles.commentAvatar}
                onError={(e) => {
                  const target = e.target as HTMLImageElement;
                  target.src = "https://images.unsplash.com/photo-1500648767791-00dcc994a43e?w=64&h=64&fit=crop&crop=face";
                }}
              />
              <input
                type="text"
                className={styles.commentInput}
                placeholder="Write a comment..."
                value={newComment}
                onChange={(e) => setNewComment(e.target.value)}
                disabled={isSubmittingComment}
              />
              <button
                type="submit"
                className={styles.commentSubmitBtn}
                disabled={!newComment.trim() || isSubmittingComment}
              >
                {isSubmittingComment ? '...' : 'Post'}
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
};

export default PostCard; 